/**
 * Contact Us Page
 * Contact support and help with iOS-first design
 * 
 * @author Varun Pandey
 */

import React, { useState } from 'react';

import { Phone, Mail, Send, MessageSquare, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Select, SelectOption } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import { useContactUsStore } from '../../stores/contactUsStore';

export const ContactUsPage: React.FC = () => {

  const [inquiryType, setInquiryType] = useState<string>('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSubmitting = useContactUsStore((s) => s.isSubmitting);
  const submitContactUs = useContactUsStore((s) => s.submitContactUs);

  const inquiryOptions: SelectOption[] = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'account', label: 'Account Issues' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'other', label: 'Other' },
  ];

  const handlePhoneClick = () => {
    window.location.href = 'tel:+917042884270';
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:connect@starclinch.com';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }

    if (!message.trim()) {
      newErrors.message = 'Please enter your message';
    } else if (message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (message.length > 500) {
      newErrors.message = 'Message cannot exceed 500 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const selectedInquiry = inquiryOptions.find((opt) => opt.value === inquiryType);

    try {
      await submitContactUs({
        inquiry_message: selectedInquiry?.label ?? inquiryType,
        message: message.trim(),
      });

      setInquiryType('');
      setMessage('');
      setErrors({});
      toast.success('Message sent successfully! We will respond within 24 hours.');
    } catch (error: any) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to send message. Please try again.';
      toast.error(errorMessage);
    }
  };

  const maxMessageLength = 500;
  const remainingChars = maxMessageLength - message.length;

  return (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      <div className="w-full md:px-4 py-10 pb-24">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="h-8 w-8 lg:h-10 lg:w-10 text-[#8E8E93]" />
            <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">Contact Us</h1>
          </div>
          <p className="text-[#8E8E93] text-sm lg:text-base">
            Get in touch with our support team
          </p>
        </div>

        <div className="space-y-6 lg:space-y-8">
          {/* Get in Touch Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-6 lg:p-8"
          >
            <h2 className="text-xl lg:text-2xl font-semibold text-white mb-6">Get in Touch</h2>
            <div className="space-y-4">
              {/* Phone */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handlePhoneClick}
                className="w-full flex items-center justify-between p-4 lg:p-5 bg-[#252527] rounded-xl border border-[#3A3A3C]/50 hover:border-[#3A3A3C] hover:bg-[#2C2C2E] transition-all group"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-[#3A3A3C] flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 lg:h-7 lg:w-7 text-[#8E8E93]" />
                  </div>
                  <div className="text-left flex-1 flex flex-col">
                    <p className="text-base lg:text-lg font-semibold text-white mb-1">
                      +91-7042884270
                    </p>
                    <p className="text-sm text-[#8E8E93]">
                      Call us directly for immediate assistance
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-[#8E8E93] group-hover:text-white transition-colors flex-shrink-0" />
              </motion.button>

              {/* Email */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleEmailClick}
                className="w-full flex items-center justify-between p-4 lg:p-5 bg-[#252527] rounded-xl border border-[#3A3A3C]/50 hover:border-[#3A3A3C] hover:bg-[#2C2C2E] transition-all group"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-[#3A3A3C] flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 lg:h-7 lg:w-7 text-[#8E8E93]" />
                  </div>
                  <div className="text-left flex-1 flex flex-col">
                    <p className="text-base lg:text-lg font-semibold text-white mb-1">
                      connect@starclinch.com
                    </p>
                    <p className="text-sm text-[#8E8E93]">
                      Send us an email for detailed inquiries
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-[#8E8E93] group-hover:text-white transition-colors flex-shrink-0" />
              </motion.button>
            </div>
          </motion.div>

          {/* Send us a Message Section */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-6 lg:p-8"
          >
            <h2 className="text-xl lg:text-2xl font-semibold text-white mb-6">Send us a Message</h2>

            <div className="space-y-6">
              {/* Inquiry Type Dropdown */}
              <div>
                <Select
                  label="General Inquiry"
                  options={inquiryOptions}
                  value={inquiryType}
                  onChange={(value) => {
                    setInquiryType(value);
                    if (errors.inquiryType) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.inquiryType;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select any option"
                  error={errors.inquiryType}
                />
              </div>

              {/* Message Textarea */}
              <div>
                <label className="mb-2 block text-sm font-medium text-white">
                  Your Message
                </label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue.length <= maxMessageLength) {
                        setMessage(newValue);
                        if (errors.message) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.message;
                            return newErrors;
                          });
                        }
                      }
                    }}
                    placeholder="Enter your message here..."
                    rows={6}
                    className={cn(
                      'w-full px-4 py-3 bg-transparent border rounded-xl text-white placeholder-[#8E8E93]',
                      'focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:border-transparent',
                      'resize-none transition-all',
                      errors.message ? 'border-red-500' : 'border-[#3A3A3C]'
                    )}
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    <span className={cn(
                      'text-xs',
                      remainingChars < 50 ? 'text-red-500' : 'text-[#8E8E93]'
                    )}>
                      {message.length}/{maxMessageLength} characters
                    </span>
                  </div>
                </div>
                {errors.message && (
                  <p className="mt-2 text-sm text-red-500">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="active"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                className="w-full py-3.5 lg:py-4 text-base lg:text-lg font-medium"
              >
                <Send className="h-5 w-5" />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </div>
  );
};